import { assign, Machine } from "xstate";

import { Session } from "@/tools/msa/models";
import { makeSession } from "@/tools/msa/processing";
import { encodeDocumentWithSnomed } from "@/tools/msa/processing/snomed";
import { extractionStates } from "@/tools/msa/utils/machine/extraction";
import { SuccessfulResponse } from "@/tools/msa/models/requests/extraction/legacy";
import { load } from "@/tools/msa/extensions/persistence";

export interface Context {
  step: number;
  retries: number;
  sessionId: string | null;
  isLoading: boolean;
  file: File | null;
  session: Session | null;
  response: SuccessfulResponse | null;
}

const makeContext = (): Context => ({
  step: 1,
  retries: 0,
  sessionId: null,
  isLoading: false,
  file: null,
  session: null,
  response: null
});

export const machine = Machine<Context>({
  initial: "idle",
  context: () => makeContext(),
  states: {
    idle: {
      entry: assign((context, event) => makeContext()),
      on: {
        SELECT: "extracting.selected"
      }
    },
    extracting: {
      on: {
        PROCESS: "processing",
        FAILED: "failed"
      },
      ...extractionStates
    },
    processing: {
      entry: assign({ step: _ => 4 }),
      invoke: {
        src: async (context, _) =>
          await makeSession(
            context.sessionId!,
            context.file!,
            context.response!
          ),
        onDone: {
          target: "processed",
          actions: assign({
            session: (context, event) => event.data,
            retries: (context, event) => 0
          })
        },
        onError: [
          {
            target: "processing",
            actions: assign({ retries: context => context.retries + 1 }),
            cond: context => context.retries === 0
          },
          { target: "failed" }
        ]
      }
    },
    reprocessing: {
      entry: assign({
        step: (context, event) => 4,
        isLoading: (context, event) => true
      }),
      invoke: {
        src: async (context, event) =>
          await encodeDocumentWithSnomed(event.document, context.session!),
        onDone: {
          target: "processed",
          actions: assign({
            retries: (context, event) => 0
          })
        },
        onError: [
          {
            target: "reprocessing",
            actions: assign({ retries: context => context.retries + 1 }),
            cond: context => context.retries === 0
          },
          { target: "processed", actions: assign({ retries: () => 0 }) }
        ]
      }
    },
    loading: {
      entry: assign({
        isLoading: (context, event) => true
      }),
      invoke: {
        src: (context, event) =>
          context.session
            ? load(event.file, context.session)
            : Promise.reject(),
        onDone: {
          target: "processed",
          actions: assign({
            session: (context, event) => event.data
          })
        },
        onError: {
          target: "processed",
          actions: [
            (context, event) => {
              alert(
                ["INVALID_FORMAT", "INVALID_SCHEMA"].includes(event.data.type)
                  ? "The selected file is not in a valid format."
                  : ["INVALID_PAGES", "DIFFERENT_PAGES"].includes(
                      event.data.type
                    )
                  ? "The selected file has a problem of integrity."
                  : event.data.type === "DIFFERENT_CHECKSUM"
                  ? "The selected file is of a different document."
                  : "An unknown error happened."
              );
            },
            (context, event) => console.error(event)
          ]
        }
      }
    },
    processed: {
      entry: assign({
        step: (context, event) => 5,
        isLoading: (context, event) => false
      }),
      on: {
        REPROCESS: "reprocessing",
        LOAD: "loading",
        CLEAR: "idle"
      }
    },
    failed: {
      id: "failed",
      entry: [
        (context, event) => {
          if (event.data.config && event.data.config.url) {
            if (
              event.data.config.headers &&
              event.data.config.headers.Authorization &&
              event.data.config.headers.Authorization.split(".").length === 3
            ) {
              try {
                const data = JSON.parse(
                  decodeURIComponent(
                    atob(
                      event.data.config.headers.Authorization.split(".")[1]
                        .replace(/-/g, "+")
                        .replace(/_/g, "/")
                    )
                      .split("")
                      .map(
                        c =>
                          "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
                      )
                      .join("")
                  )
                );
                console.log("iat", new Date(data.iat * 1000));
                console.log("exp", new Date(data.exp * 1000));
              } catch (e) {
                console.log("couldn't process jwt");
              }
            }
          }
        },
        assign({
          isLoading: (context, event) => false
        })
      ],
      on: {
        RETRY: "idle"
      }
    }
  }
});
